// ============================================
// useAiChatServer — 씬 클라이언트 훅 (C′ 통합설계 §8, ai_manager_refact)
// ============================================
// 에이전트 루프는 node-agent 서버가 실행 — 브라우저는 질문 접수(POST api3/ai-agent/run/accept) 후
// 결과를 짧게 반복 조회(GET api3/ai-agent/run/{turnId})한다. 연결을 오래 붙잡지 않아 재배포에도 답을 잃지 않는다.
// useAiChat(브라우저 루프)과 동일 반환 인터페이스 유지 → AiChattingView 등 기존 컴포넌트 무수정 재사용.
// OpenAI 키·에이전트 세션·버퍼·텔레메트리는 전부 서버 소유. 세션 목록/복원/평가/SMS 발송은 기존 Spring API 직접 호출 유지.

import { useState, useEffect, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import FetchModule from '../../Model/Share/Network/FetchModule';
import { RESPONSE_FAILED_RETRY, CANCELLED_RESPONSE, TOOL_CALLING_LOG } from '../../AiManager/Model/aiConfig';
import { createSmsHandlers, restoreSmsFromSave } from '../../AiManager/Transaction/smsHandlers';
import { detectResponseLanguage } from '../../Agent/Tools/responseLanguage';
import { parseProgress, initialProgressLine } from './aiProgressLine';
import { runAcceptedTurn, stopTurn, decodeTurnRender } from './aiAgentTurn';

// 토큰 소진 안내 — useAiChat.js 동일 문구 (dual sync) //
const TOKEN_EXHAUSTED_MESSAGE = '이번 달 AI 토큰을 모두 사용했어요. 다음 달에 다시 이용하시거나 토큰을 충전해 주세요.';

export default function useAiChatServer() {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const [tokenExhausted, setTokenExhausted] = useState(false);
  const [usagePercent, setUsagePercent] = useState(0);
  const [storeName, setStoreName] = useState('');

  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(`session_${uuidv4()}`);
  const [messageRatings, setMessageRatings] = useState({});

  const chatMessagesContainerRef = useRef(null);
  const chatMessagesRef = useRef(chatMessages);
  chatMessagesRef.current = chatMessages;

  // 메시지별 대화 저장 메타 { [messageId]: { conversationId, userInput, intention, elapsedMs } } //
  const conversationMetaRef = useRef({});

  // 진행 중인 턴 — 요청 취소와 로딩 타이머를 질문별 객체에 보관한다 //
  // 종전에는 중단 여부·로딩 타이머를 훅 전체가 ref 하나로 공유했다. 중단 직후 다시 질문하면 새 턴이 그 값을 되돌려
  // 잠들어 있던 옛 폴링이 되살아났고, 옛 턴의 마무리(finally)가 새 턴의 타이머를 끄고 입력창을 열었다.
  // 턴별 객체면 옛 턴은 제 객체의 cancelled 만 보므로 새 턴과 섞이지 않는다 //
  const activeTurnRef = useRef(null);
  const tokenExhaustedRef = useRef(false);

  // ============================================
  // 사용량 게이지 초기화 — 권한 조회 (키 복호화 없음: 키는 서버 소유) //
  // ============================================
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2('ai_manage/permission', 'GET');

          if (responseData.status === 200 && responseData.data) {
            const data = responseData.data;

            setStoreName(data.storeName || '');
            if (typeof data.usagePercent === 'number') setUsagePercent(data.usagePercent);

            if (data.tokenExhausted === true) {
              setUsagePercent(100);
              tokenExhaustedRef.current = true;
              setTokenExhausted(true);
            }
          }
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[AgentServer] permission error:', error);
        }
      }
    )();
  }, []);

  // ============================================
  // 채팅 스크롤 처리 (useAiChat 스페이서 로직 동일 — dual sync) //
  // ============================================
  const scrollTargetIdRef = useRef(null);
  const activeAiMsgIdRef = useRef(null);
  const initialSpacerHeightRef = useRef(0);

  function resetSpacer() {
    activeAiMsgIdRef.current = null;
    initialSpacerHeightRef.current = 0;
    const container = chatMessagesContainerRef.current;
    if (container) container.style.setProperty('--after-spacer-height', '0px');
  }

  useEffect(() => {
    // 1. 사용자 메시지 전송 직후 → 빈공간 생성 + 상단 스크롤
    const targetId = scrollTargetIdRef.current;
    if (targetId) {
      scrollTargetIdRef.current = null;
      const container = chatMessagesContainerRef.current;
      if (container) {
        const spacer = container.clientHeight;
        initialSpacerHeightRef.current = spacer;
        container.style.setProperty('--after-spacer-height', `${spacer}px`);
        requestAnimationFrame(() => {
          const target = container.querySelector(`[data-message-id="${targetId}"]`);
          if (target) {
            target.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        });
      }
      return;
    }

    // 2. AI 응답 타이핑 중 → 응답 높이만큼 빈공간 차감
    if (activeAiMsgIdRef.current && initialSpacerHeightRef.current > 0) {
      const container = chatMessagesContainerRef.current;
      if (container) {
        const aiMsgEl = container.querySelector(`[data-message-id="${activeAiMsgIdRef.current}"]`);
        const aiHeight = aiMsgEl ? aiMsgEl.offsetHeight : 0;
        const spacerHeight = Math.max(0, initialSpacerHeightRef.current - aiHeight);
        container.style.setProperty('--after-spacer-height', `${spacerHeight}px`);
      }
    }
  }, [chatMessages]);

  // 3. 응답 종료 시 → 빈공간 제거 //
  useEffect(() => {
    if (!isResponding) resetSpacer();
  }, [isResponding]);

  // 4. 화면 리사이즈 시 → 빈공간 제거 //
  useEffect(() => {
    function onResize() { resetSpacer(); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 5. 언마운트 cleanup — 진행 중 요청 중단 //
  useEffect(() => {
    return () => {
      stopTurn(activeTurnRef.current);
      activeTurnRef.current = null;
    };
  }, []);

  // ============================================
  // 채팅 메시지 관리 (추가/업데이트 — useAiChat 동일) //
  // ============================================
  // 사용자 메시지 추가 //
  function addUserMessage(text) {
    const id = `msg_${uuidv4()}`;
    setChatMessages(prev => [...prev, { id, text, isAi: false, timestamp: Date.now() }]);
    return id;
  }

  // AI 메시지 추가 — richText: 답 글을 마크다운 + 한 줄 바꿈 보존으로 그린다(AnswerMarkdown). 레거시 /ai 훅은 이 값이 없다 //
  function addAiMessage(text = '') {
    const id = `msg_${uuidv4()}`;
    setChatMessages(prev => [...prev, { id, text, isAi: true, richText: true, timestamp: Date.now() }]);
    return id;
  }

  // AI 메시지 업데이트 (flags 값이 함수이면 이전 값을 인자로 전달하여 부분 업데이트) //
  function updateAiMessage(id, text, flags = {}) {
    setChatMessages(prev => prev.map(msg => {
      if (msg.id !== id) return msg;

      const resolved = {};
      for (const [key, val] of Object.entries(flags)) {
        resolved[key] = typeof val === 'function' ? val(msg[key]) : val;
      }

      return {
        ...msg,
        ...(text !== undefined ? { text } : {}),
        ...resolved,
      };
    }));
  }

  // 질문별 언어와 예고 상태를 고정한다. 유효한 서버 예고 뒤에는 시간 문구를 멈춘다. //
  function showToolLoading(id, userInput, turn) {
    const language = detectResponseLanguage(userInput || '');
    const started = Date.now();

    updateAiMessage(id, initialProgressLine(language, 0), { isToolLoading: true, progressLang: language, progress: null });
    turn.loadingTimer = setInterval(() => {
      if (turn.cancelled || activeTurnRef.current !== turn || turn.hasProgress) return;

      updateAiMessage(id, initialProgressLine(language, Date.now() - started));
    }, 500);
  }

  // 로딩 해제 → 일반 메시지로 전환 //
  function clearToolLoading(id) {
    setChatMessages(prev => prev.map(msg => msg.id === id ? { ...msg, isToolLoading: false } : msg));
  }

  // 타이핑 효과 (최종 응답을 한 글자씩 — useAiChat 동일 3자/15ms) //
  async function typeMessage(id, text, turn, chunkSize = 3, delayMs = 15) {
    clearToolLoading(id);
    let current = '';
    for (let i = 0; i < text.length; i += chunkSize) {
      if (turn.cancelled || activeTurnRef.current !== turn) break;
      current += text.slice(i, i + chunkSize);
      updateAiMessage(id, current);
      await new Promise(r => setTimeout(r, delayMs));
    }
    if (!turn.cancelled && activeTurnRef.current === turn) {
      updateAiMessage(id, text);
    }
  }

  // ============================================
  // 평가 (useAiChat 동일 — POST ai-log/rating) //
  // ============================================
  const handleRateMessage = async (messageId, score, reason = null, comment = null) => {
    const meta = conversationMetaRef.current[messageId];
    if (!meta?.conversationId) return;

    const prevRating = messageRatings[messageId] || null;

    setMessageRatings(prev => ({ ...prev, [messageId]: { score, reason, comment } }));

    const fetchModule = new FetchModule();
    let isSaved = false;

    try {
      const res = await fetchModule.postDataVersion2('ai-log/rating', 'POST', {
        conversationId: meta.conversationId,
        sessionId,
        score,
        reason,
        comment,
      });
      isSaved = res?.status === 200;
    }
    catch (error) {
      isSaved = false;
    }

    // 저장 실패 시 조용히 이전 상태로 복귀 (채팅 동작 무영향) //
    if (!isSaved) setMessageRatings(prev => ({ ...prev, [messageId]: prevRating }));
  };

  // 평가 가능 여부 — 저장된 turn(conversationId 보유)만 평가 UI 노출 //
  const isMessageRatable = (messageId) => !!conversationMetaRef.current[messageId]?.conversationId;

  // ============================================
  // 턴 실행 — Spring 접수·상태 폴링 //
  // ============================================
  async function requestAgentRun(userInput, aiMessageId, turn) {
    return runAcceptedTurn({
      input: userInput,
      sessionId,
      turn,
      onProgress: progressJson => {
        if (turn.cancelled || activeTurnRef.current !== turn) return;

        const progress = parseProgress(progressJson);

        if (progress === null) return;

        turn.hasProgress = true;
        clearInterval(turn.loadingTimer);
        updateAiMessage(aiMessageId, undefined, { progress });
      },
    });
  }

  // 메시지 전송 //
  const handleSend = async () => {
    // 토큰 소진 — 서버 호출 없이 안내 메시지만 노출 //
    if (tokenExhaustedRef.current) {
      if (!chatInput.trim() || isResponding || activeTurnRef.current) return;

      setChatInput('');
      addAiMessage(TOKEN_EXHAUSTED_MESSAGE);
      return;
    }

    if (!chatInput.trim() || isResponding || activeTurnRef.current) return;

    const userInput = chatInput.trim();
    setChatInput('');
    setIsResponding(true);
    const turn = { controller: new AbortController(), cancelled: false, hasProgress: false };
    activeTurnRef.current = turn;

    const userMsgId = addUserMessage(userInput);
    scrollTargetIdRef.current = userMsgId;
    const aiMessageId = addAiMessage('');
    activeAiMsgIdRef.current = aiMessageId;

    // [로딩 즉시 표시] 서버 응답을 기다리지 않고 제출 직후 로딩 문구 노출 //
    showToolLoading(aiMessageId, userInput, turn);

    try {
      const data = await requestAgentRun(userInput, aiMessageId, turn);

      if (turn.cancelled || activeTurnRef.current !== turn) return;
      if (!data || data.state !== 'done') throw new Error(data?.message || 'agent run failed');

      clearInterval(turn.loadingTimer);
      const { text: renderedText, ...rendered } = decodeTurnRender(data.renderJson);

      // 평가용 대화 메타 저장 //
      if (data.turnId) {
        conversationMetaRef.current[aiMessageId] = {
          conversationId: data.turnId,
          userInput,
          intention: data.intention || null,
          elapsedMs: data.elapsedMs || null,
        };
      }

      // 사용량 게이지 갱신 + 소진 반영 //
      if (typeof data.usagePercent === 'number') setUsagePercent(data.usagePercent);

      if (data.tokenExhausted === true) {
        tokenExhaustedRef.current = true;
        setTokenExhausted(true);
        setUsagePercent(100);
      }

      const finalResponse = (renderedText?.trim() ? renderedText : data.finalText) || RESPONSE_FAILED_RETRY;
      await typeMessage(aiMessageId, finalResponse, turn);
      if (turn.cancelled || activeTurnRef.current !== turn) return;

      // 타이핑 종료 후 표/차트/SMS 부착 — 서버가 채널 분리(displayItems/vizData/...)해서 내려줌 //
      // salesViewData는 weeksData(렌더 페이로드) 있을 때만 부착 — 코어 v1 요약판은 로그/검증용이라 미부착 (SalesViewRenderer:38 weeksData.map 무가드 크래시 차단) //
      updateAiMessage(aiMessageId, finalResponse, {
        isToolLoading: false,
        isNewResponse: true,
        ...rendered,
        ...(rendered.smsRecommendation ? { smsRecommendation: restoreSmsFromSave(rendered.smsRecommendation) } : {}),
      });

      // 서버가 첫 턴에 세션 타이틀 저장 → 목록 갱신 //
      loadSessions();
    }
    catch (error) {
      if (TOOL_CALLING_LOG) console.error('[AgentServer] handleSend error:', error);
      if (turn.cancelled || activeTurnRef.current !== turn) return;

      const language = detectResponseLanguage(userInput);
      const busyMessage = language === 'en' ? 'The previous answer is still being prepared. Please try again once it is finished.'
        : language === 'ja' ? '前の回答をまだ準備しています。完了後にもう一度お試しください。'
          : '앞선 답변을 아직 준비하고 있어요. 완료 후 다시 질문해 주세요.';

      updateAiMessage(aiMessageId, error.code === 'TURN_RUNNING' ? busyMessage : RESPONSE_FAILED_RETRY, { isToolLoading: false });
    }
    finally {
      clearInterval(turn.loadingTimer);
      if (activeTurnRef.current === turn) {
        activeTurnRef.current = null;
        setIsResponding(false);
      }
    }
  };

  // 응답 중단 — 클라이언트 측 중단(fetch abort). 서버 턴은 계속 완료·저장됨 (v1) //
  const handleStop = () => {
    const turn = activeTurnRef.current;
    stopTurn(turn);
    activeTurnRef.current = null;
    if (turn) updateAiMessage(activeAiMsgIdRef.current, CANCELLED_RESPONSE, { isToolLoading: false });
    setIsResponding(false);
  };

  // ============================================
  // 세션 관련 함수 (useAiChat 동일 — Spring API 직접 호출) //
  // ============================================
  // 세션 목록 초기 로드 //
  useEffect(() => {
    loadSessions();
  }, []);

  // 세션 목록 불러오기 //
  const loadSessions = () => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2('ai-chat/sessions', 'GET');

          if (responseData.status === 200) {
            setSessions(responseData.data || []);
          }
        }
        catch (error) { /* 무시 */ }
      }
    )();
  };

  // 세션 대화 내역 불러오기 — 화면 복원은 renderJson, 버퍼 복원은 서버(session-state) 책임 //
  const loadConversations = (targetSessionId) => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2(`ai-chat/sessions/${targetSessionId}`, 'GET');

          if (responseData.status === 200 && responseData.data) {
            const messages = [];
            const restoredRatings = {};

            responseData.data.forEach(entry => {
              messages.push({ id: `msg_${uuidv4()}`, text: entry.requestMessage, isAi: false });

              // AI 응답 복원 — JSON(텍스트 + 테이블 + SMS) 또는 텍스트 원문 //
              // 진행 기록(entry.progressJson)은 화면에 그리지 않는다 — 답이 온 뒤의 「살펴본 과정」은 뺐고(2026-09-23 사용자 판단),
              // 기록은 서버가 conversation_log.progress_json 에 남겨 운영 분석용으로 쓴다 //
              let aiMsg = { id: `msg_${uuidv4()}`, isAi: true, richText: true };
              try {
                const parsed = JSON.parse(entry.responseMessage);
                if (parsed?.text !== undefined) {
                  aiMsg.text = parsed.text || '';
                  if (parsed.displayItems) aiMsg.displayItems = parsed.displayItems;
                  if (Array.isArray(parsed.extraDisplays) && parsed.extraDisplays.length > 0) aiMsg.extraDisplays = parsed.extraDisplays;
                  if (parsed.smsRecommendation) aiMsg.smsRecommendation = restoreSmsFromSave(parsed.smsRecommendation);
                  if (parsed.salesViewData) aiMsg.salesViewData = parsed.salesViewData;
                  if (parsed.revenueChartData) aiMsg.revenueChartData = parsed.revenueChartData;
                  if (parsed.vizData) aiMsg.vizData = parsed.vizData;
                }
                else {
                  aiMsg.text = entry.responseMessage;
                }
              }
              catch {
                aiMsg.text = entry.responseMessage;
              }
              messages.push(aiMsg);

              // 복원된 메시지에 대해 conversationMetaRef 설정 //
              if (entry.id) {
                conversationMetaRef.current[aiMsg.id] = {
                  conversationId: entry.id,
                  userInput: entry.requestMessage,
                  intention: entry.intention || null,
                  elapsedMs: entry.elapsedMs || null,
                };

                // 저장된 사용자평가 복원 //
                if (entry.score === 1 || entry.score === -1) {
                  restoredRatings[aiMsg.id] = {
                    score: entry.score,
                    reason: entry.reason ?? null,
                    comment: entry.comment ?? null,
                  };
                }
              }
            });

            setSessionId(targetSessionId);
            setChatMessages(messages);
            setMessageRatings(restoredRatings);
            resetSpacer();
          }
        }
        catch (error) { /* 무시 */ }
      }
    )();
  };

  // 세션 삭제 //
  const deleteSession = (targetSessionId) => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2(`ai-chat/sessions/${targetSessionId}`, 'DELETE');

          if (responseData.status === 200) {
            if (sessionId === targetSessionId) handleNewChat();

            loadSessions();
          }
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[Session] delete error:', error);
        }
      }
    )();
  };

  // 새 채팅 세션 생성 — 서버 세션은 TTL로 자연 소멸 //
  const handleNewChat = () => {
    handleStop();
    const newSessionId = `session_${uuidv4()}`;
    setSessionId(newSessionId);
    setChatMessages([]);
    setMessageRatings({});
    resetSpacer();
  };

  // ============================================
  // SMS 핸들러 — 발송은 기존 Spring API 재사용 //
  // ============================================
  // 재작성(generate)은 LLM 필요 → clientRef null 가드로 자연 강등 (P1: 서버 위임 예정) //
  const { handleSmsSend, handleIndividualSmsSend, handleIndividualSmsGenerate } = useMemo(
    () =>
      createSmsHandlers({
        clientRef: { current: null },
        storeName,
        sessionId,
        chatMessagesRef,
        conversationMetaRef,
        updateAiMessage,
        loggedCompletion: null,
        resendTurnWithSms: null,
      }),
    [storeName, sessionId]
  );

  return {
    // 상태
    chatMessages,
    chatInput,
    isResponding,
    tokenExhausted,
    usagePercent,
    sessions,
    sessionId,
    chatMessagesContainerRef,
    messageRatings,

    // 액션
    setChatInput,
    handleSend,
    handleStop,
    handleNewChat,
    loadConversations,
    deleteSession,
    handleRateMessage,
    isMessageRatable,

    // SMS 핸들러 //
    handleSmsSend,
    handleIndividualSmsSend,
    handleIndividualSmsGenerate,
  };
}
